
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useBadgeAPI } from '@/hooks/useBadgeAPI';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  expires_at?: string;
  data?: any;
}

export const useNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { setBadgeCount, clearBadge } = useBadgeAPI();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log('Fetching notifications for user:', user.id);
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .or('expires_at.is.null,expires_at.gt.now()')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        throw error;
      }

      console.log('Fetched notifications:', data);
      setNotifications(data || []);
      const unread = data?.filter(n => !n.is_read).length || 0;
      setUnreadCount(unread);
      
      // Update badge count
      setBadgeCount(unread);

    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել ծանուցումները",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const markAsRead = async (notificationId: string) => {
    try {
      console.log('Marking notification as read:', notificationId);
      
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        throw error;
      }

      // Real-time update
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      
      const newUnreadCount = Math.max(0, unreadCount - 1);
      setUnreadCount(newUnreadCount);
      
      // Update badge count
      setBadgeCount(newUnreadCount);

    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      console.log('Marking all notifications as read for user:', user.id);
      
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        throw error;
      }

      // Real-time update
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      
      setUnreadCount(0);
      
      // Clear badge
      clearBadge();

    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const sendNotification = async (
    recipientType: string,
    title: string,
    message: string,
    expiresAt?: string,
    targetUserId?: string
  ) => {
    try {
      console.log('Sending notification:', { recipientType, title, message, expiresAt, targetUserId });
      
      // Get recipient user IDs based on type
      let userIds: string[] = [];

      if (recipientType === 'user_id' && targetUserId) {
        console.log('Sending to specific user ID:', targetUserId);
        userIds = [targetUserId];
      } else if (recipientType === 'all_users') {
        console.log('Getting all approved users...');
        const { data: allUsers, error } = await supabase
          .from('profiles')
          .select('id')
          .eq('approval_status', 'approved');
        
        if (error) {
          console.error('Error fetching all users:', error);
          throw error;
        }
        userIds = allUsers?.map(u => u.id) || [];
        console.log('All users found:', userIds.length);
      } else if (recipientType === 'students') {
        console.log('Getting students...');
        const { data: students, error } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'student');
        
        if (error) {
          console.error('Error fetching students:', error);
          throw error;
        }
        userIds = students?.map(s => s.user_id) || [];
        console.log('Students found:', userIds.length);
      } else if (recipientType === 'lecturers') {
        console.log('Getting lecturers...');
        const { data: lecturers, error } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'lecturer');
        
        if (error) {
          console.error('Error fetching lecturers:', error);
          throw error;
        }
        userIds = lecturers?.map(l => l.user_id) || [];
        console.log('Lecturers found:', userIds.length);
      } else if (recipientType === 'admins') {
        console.log('Getting admins...');
        const { data: admins, error } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'admin');
        
        if (error) {
          console.error('Error fetching admins:', error);
          throw error;
        }
        userIds = admins?.map(a => a.user_id) || [];
        console.log('Admins found:', userIds.length);
      }

      if (userIds.length === 0) {
        console.warn('No recipients found for type:', recipientType);
        return { success: false, error: 'No recipients found' };
      }

      // Create notifications for all recipients
      const notificationsToInsert = userIds.map(userId => ({
        user_id: userId,
        title,
        message,
        type: 'admin_announcement',
        expires_at: expiresAt || null,
        data: { sent_by: 'admin' }
      }));

      console.log('Inserting notifications:', notificationsToInsert.length);

      const { error } = await supabase
        .from('notifications')
        .insert(notificationsToInsert);

      if (error) {
        console.error('Error inserting notifications:', error);
        throw error;
      }

      console.log('Notifications sent successfully');

      // Send push notifications if users have subscriptions
      try {
        const { error: pushError } = await supabase.functions.invoke('send-push-notification', {
          body: {
            userIds,
            title,
            message,
            data: { type: 'admin_announcement', sent_by: 'admin' }
          }
        });

        if (pushError) {
          console.error('Error sending push notifications:', pushError);
        } else {
          console.log('Push notifications sent successfully');
        }
      } catch (pushError) {
        console.error('Error invoking push notification function:', pushError);
      }

      return { success: true };

    } catch (error) {
      console.error('Error sending notification:', error);
      return { success: false, error };
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Subscribe to real-time updates
    if (user) {
      console.log('Setting up real-time subscription for user:', user.id);
      
      const channel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('New notification received:', payload);
            const newNotification = payload.new as Notification;
            
            // Real-time update without refetch
            setNotifications(prev => [newNotification, ...prev]);
            const newUnreadCount = unreadCount + 1;
            setUnreadCount(newUnreadCount);
            
            // Update badge count
            setBadgeCount(newUnreadCount);
            
            // Show toast for new notification
            toast({
              title: newNotification.title,
              description: newNotification.message,
              duration: 5000
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Notification updated:', payload);
            const updatedNotification = payload.new as Notification;
            
            // Real-time update
            setNotifications(prev =>
              prev.map(n =>
                n.id === updatedNotification.id ? updatedNotification : n
              )
            );
            
            // Update unread count
            const wasRead = payload.old?.is_read;
            const isRead = updatedNotification.is_read;
            
            let newUnreadCount = unreadCount;
            if (!wasRead && isRead) {
              newUnreadCount = Math.max(0, unreadCount - 1);
              setUnreadCount(newUnreadCount);
            } else if (wasRead && !isRead) {
              newUnreadCount = unreadCount + 1;
              setUnreadCount(newUnreadCount);
            }
            
            // Update badge count if unread status changed
            if (wasRead !== isRead) {
              setBadgeCount(newUnreadCount);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Notification deleted:', payload);
            const deletedNotification = payload.old as Notification;
            
            // Real-time update
            setNotifications(prev =>
              prev.filter(n => n.id !== deletedNotification.id)
            );
            
            // Update unread count if deleted notification was unread
            if (!deletedNotification.is_read) {
              const newUnreadCount = Math.max(0, unreadCount - 1);
              setUnreadCount(newUnreadCount);
              setBadgeCount(newUnreadCount);
            }
          }
        )
        .subscribe();

      return () => {
        console.log('Cleaning up notification subscription');
        supabase.removeChannel(channel);
      };
    }
  }, [user, toast, fetchNotifications]);

  return {
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    sendNotification
  };
};
